// File: src/pages/ProfileEditPage.jsx

import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaInstagram, FaLinkedin, FaRegImage, FaFolderOpen } from 'react-icons/fa';
import { LuPencil,LuX, LuArrowLeft } from 'react-icons/lu';

import EventHelpTooltip from '../components/EventPreview/EventHelpTooltip';
import { ReactComponent as TribeLogo } from '../assets/TribeLogo.svg';
import { AuthContext } from '../context/AuthContext';
import UnsavedChangesBottomSheet from '../components/UnsavedChangesBottomSheet';

function isPWA() {
  return (
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    window.navigator.standalone === true
  );
}

// Suggested interests
const PRESET_INTERESTS = [
  'Hiking', 'Painting', 'Coding', 'Music', 'Art', 'Reading',
  'Travel', 'Photography', 'Gaming', 'Cooking', 'Yoga',
  'Dancing', 'Sports', 'Movies', 'Fitness', 'Meditation',
  'Board Games', 'Gardening',
];

const interestEmojis = {
  Hiking: '🥾',
  Painting: '🎨',
  Coding: '💻',
  Music: '🎵',
  Art: '🖼️',
  Reading: '📚',
  Travel: '✈️',
  Photography: '📸',
  Gaming: '🎮',
  Cooking: '🍳',
  Yoga: '🧘',
  Dancing: '💃',
  Sports: '🏀',
  Movies: '🎬',
  Fitness: '🏋️',
  Meditation: '🧘',
  'Board Games': '🎲',
  Gardening: '🌱',
};

const MIN_ABOUT_LENGTH = 25;

/** Extract the user’s handle from a full URL. */
function extractHandleFromUrl(platform, fullUrl = '') {
  const map = {
    instagram: 'https://instagram.com/',

    linkedin: 'https://www.linkedin.com/in/',
  };
  const prefix = map[platform] || '';
  if (fullUrl.startsWith(prefix)) return fullUrl.slice(prefix.length);
  return fullUrl;
}

/* ---------- Header (align with Settings/UpdateAccount) ---------- */
const headerContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  position: 'sticky',
  top: 0,
  background: '#fff',
  zIndex: 100,
  fontFamily: "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
};
const headerLogoRowStyle = {
  display: 'flex',
  justifyContent: 'center',
  paddingTop: '0.5rem',
};
const logoSvgStyle = { width: 72, height: 'auto' };
const headerNavRowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0.5rem 1rem',
  position: 'relative',
  height: 44,
};
const previousBackButtonStyle = {
  position: 'absolute',
  left: 16,
  background: '#000',
  color: '#fff',
  borderRadius: 999,
  width: 40,
  height: 28,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  border: 'none',
};
const pageTitlePlainStyle = {
  fontSize: '0.95rem',
  fontWeight: 900,
  lineHeight: 1,
  textTransform: 'uppercase',
  letterSpacing: 0.2,
};

export default function ProfileEditPage() {
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);
  const [user, setUser] = useState(null);

  // Basic fields
  const [gender, setGender] = useState('');
  const [age, setAge] = useState('');
  const [overview, setOverview] = useState('');
  const [interests, setInterests] = useState([]);
  const [socialHandles, setSocialHandles] = useState({
    instagram: '',
    linkedin: '',
  });

  // Photo handling
  const [profilePicUrl, setProfilePicUrl] = useState('/default-avatar.png');
  const [newProfilePicFile, setNewProfilePicFile] = useState(null);

  // Tabs
  const [activeTab, setActiveTab] = useState(0);
  const [newInterest, setNewInterest] = useState('');

  const [aboutFocused, setAboutFocused] = useState(false);
  const [anyInputFocused, setAnyInputFocused] = useState(false);

  // Action sheet for picking profile pic
  const [showProfileActionSheet, setShowProfileActionSheet] = useState(false);

  // Unsaved changes
  const [isDirty, setIsDirty] = useState(false);
  const [showUnsavedSheet, setShowUnsavedSheet] = useState(false);

  // Keep a snapshot of original user fields to detect changes
  const [originalData, setOriginalData] = useState({
    overview: '',
    interests: [],
    socials: { instagram: '', linkedin: '' },
    hasPic: false,
  });

  // Refs for file inputs
  const libraryInputRef = useRef(null);
  const filesInputRef = useRef(null);
  const profilePicInputRef = useRef(null);

  // Lock scroll when focusing "About"
  const scrollPositionRef = useRef(0);
  useEffect(() => {
    let timeoutId = null;
    const originalOverflow = document.body.style.overflow;
    const forceScrollToTop = () => {
      window.scrollTo({ top: 0, behavior: 'instant' });
      timeoutId = setTimeout(() => window.scrollTo({ top: 0, behavior: 'instant' }), 100);
    };
    const preventTouchMove = (e) => e.preventDefault();

    if (aboutFocused) {
      scrollPositionRef.current = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollPositionRef.current}px`;
      document.body.style.left = '0';
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      window.visualViewport?.addEventListener('resize', forceScrollToTop);
      forceScrollToTop();
      document.addEventListener('touchmove', preventTouchMove, { passive: false });

      return () => {
        window.visualViewport?.removeEventListener('resize', forceScrollToTop);
        clearTimeout(timeoutId);
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.width = '';
        document.body.style.overflow = originalOverflow;
        document.removeEventListener('touchmove', preventTouchMove);
        window.scrollTo({ top: scrollPositionRef.current });
      };
    }
  }, [aboutFocused]);

  // Dim system bar on sheet open (like other pages)
  useEffect(() => {
    const themeMetaTag = document.querySelector('meta[name="theme-color"]');
    if (showProfileActionSheet) {
      scrollPositionRef.current = window.scrollY || window.pageYOffset;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollPositionRef.current}px`;
      document.body.style.width = '100%';
      document.body.classList.add('overlay-active');
      document.documentElement.classList.add('overlay-active');
      document.getElementById('root')?.classList.add('overlay-active');
      if (themeMetaTag) themeMetaTag.content = '#999998';
    } else {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.classList.remove('overlay-active');
      document.documentElement.classList.remove('overlay-active');
      document.getElementById('root')?.classList.remove('overlay-active');
      if (themeMetaTag) themeMetaTag.content = '#ffffff';
      requestAnimationFrame(() => window.scrollTo(0, scrollPositionRef.current));
    }
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.classList.remove('overlay-active');
      document.documentElement.classList.remove('overlay-active');
      document.getElementById('root')?.classList.remove('overlay-active');
      if (themeMetaTag) themeMetaTag.content = '#ffffff';
    };
  }, [showProfileActionSheet]);

  // Load user
  useEffect(() => {
    if (!currentUser?._id) return;
    (async () => {
      try {
        const resp = await axios.get(`${process.env.REACT_APP_API_URL}/users/${currentUser._id}`);
        setUser(resp.data);
      } catch (err) {
        console.error('[ProfileEditPage] fetch error =>', err);
      }
    })();
  }, [currentUser]);

  // Populate fields & snapshot
  useEffect(() => {
    if (!user) return;
    setOverview(user.overview || '');
    const userInterests = Array.isArray(user.interests) ? user.interests : [];
    setInterests(userInterests);
    setGender(user.gender || '');

    if (user.dateOfBirth) {
      const dob = new Date(user.dateOfBirth);
      const now = new Date();
      let userAge = now.getFullYear() - dob.getFullYear();
      const m = now.getMonth() - dob.getMonth();
      if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) userAge--;
      setAge(userAge);
    } else {
      setAge('');
    }

    const igUrl = user.socials?.instagram || '';
    const lnUrl = user.socials?.linkedin || '';
    const extractedSocials = {
      instagram: extractHandleFromUrl('instagram', igUrl),
      linkedin: extractHandleFromUrl('linkedin', lnUrl),
    };
    setSocialHandles(extractedSocials);

    const initialPic = user?.profilePicUrl || '/default-avatar.png';
    setProfilePicUrl(initialPic);

    setOriginalData({
      overview: user.overview || '',
      interests: [...userInterests],
      socials: { ...extractedSocials },
      hasPic: Boolean(user.profilePicUrl),
    });

    setIsDirty(false);
  }, [user]);

  function didOnlyRemoveInterestsBelow3() {
    if (interests.length >= 3) return false;
    if ((overview || '') !== (originalData.overview || '')) return false;

    const s1 = originalData.socials;
    const s2 = socialHandles;
    if (s1.instagram !== s2.instagram ||  s1.linkedin !== s2.linkedin) {
      return false;
    }

    if (newProfilePicFile != null) return false;

    for (let i of interests) {
      if (!originalData.interests.includes(i)) return false;
    }
    return true;
  }

  // Save
  const handleSave = async () => {
    if (!user) return;
    if (overview.trim().length < MIN_ABOUT_LENGTH) return;
    if (interests.length < 3) return;
    try {
      if (newProfilePicFile) {
        const formData = new FormData();
        formData.append('profilePic', newProfilePicFile);
        await axios.post(
          `${process.env.REACT_APP_API_URL}/users/${user._id}/profile-picture`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      }
      await axios.put(`${process.env.REACT_APP_API_URL}/users/${user._id}`, {
        overview,
        interests,
        socialHandles,
      });
      const updated = await axios.get(`${process.env.REACT_APP_API_URL}/users/${user._id}`);
      setUser(updated.data);
      setIsDirty(false);
      navigate('/profile');
    } catch (err) {
      console.error('[ProfileEditPage] save error =>', err);
      alert(err.response?.data?.message || err.message);
    }
  };

  // Cancel / Back
  const handleCancel = () => {
    if (!isDirty || didOnlyRemoveInterestsBelow3()) return navigate('/profile');
    setShowUnsavedSheet(true);
  };
  const handleBack = () => {
    if (!isDirty || didOnlyRemoveInterestsBelow3()) return navigate(-1);
    setShowUnsavedSheet(true);
  };

  // Field handlers
  function handleProfilePicChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setNewProfilePicFile(file);
    setIsDirty(true);
    const reader = new FileReader();
    reader.onloadend = () => setProfilePicUrl(reader.result);
    reader.readAsDataURL(file);
  }
  function handleAddInterest(val) {
    const trimmed = val.trim();
    if (trimmed && !interests.includes(trimmed)) {
      setInterests([...interests, trimmed]);
      setIsDirty(true);
    }
  }
  function handleRemoveInterest(val) {
    setInterests((prev) => prev.filter((i) => i !== val));
    setIsDirty(true);
  }
  function handleInterestKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddInterest(newInterest);
      setNewInterest('');
    }
  }
  function handlePrefixedChange(platform, rawValue) {
    const noAt = rawValue.startsWith('@') ? rawValue.slice(1) : rawValue;
    setSocialHandles({ ...socialHandles, [platform]: noAt });
    setIsDirty(true);
  }
  function handleOverviewChange(e) {
    setOverview(e.target.value);
    setIsDirty(true);
  }

  // Action sheet
  function handleLibraryClick() {
    libraryInputRef.current?.click();
  }
  function handleLibraryChange(e) {
    const file = e.target.files?.[0];
    if (file) {
      setNewProfilePicFile(file);
      setIsDirty(true);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePicUrl(reader.result);
        setShowProfileActionSheet(false);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = null;
  }
  function handleFilesClick() {
    filesInputRef.current?.click();
  }
  function handleFilesChange(e) {
    const file = e.target.files?.[0];
    if (file) {
      setNewProfilePicFile(file);
      setIsDirty(true);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePicUrl(reader.result);
        setShowProfileActionSheet(false);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = null;
  }

  function renderProfileActionSheet() {
    if (!showProfileActionSheet) return null;
    return (
      <>
        <div style={styles.backdrop} onClick={() => setShowProfileActionSheet(false)}>
          <div
            style={{ ...styles.sheetContainer, marginBottom: isPWA() ? 40 : 8 }}
            onClick={(e) => e.stopPropagation()}
          >
            <input
              ref={libraryInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleLibraryChange}
            />
            <input
              ref={filesInputRef}
              type="file"
              accept="*/*"
              style={{ display: 'none' }}
              onChange={handleFilesChange}
            />

            <div style={styles.optionContainer}>
              <div style={{ ...styles.optionRow, ...styles.optionRowTop }} onClick={handleLibraryClick}>
                <span>Photo Library</span>
                <FaRegImage style={styles.iconRight} />
              </div>
              <div style={{ ...styles.optionRow, ...styles.optionRowBottom }} onClick={handleFilesClick}>
                <span>Choose from Files</span>
                <FaFolderOpen style={styles.iconRight} />
              </div>
            </div>
            <div style={styles.cancelRow} onClick={() => setShowProfileActionSheet(false)}>
              Cancel
            </div>
          </div>
        </div>

        <style>
          {`
            @keyframes slideUp {
              from { transform: translateY(100%); opacity: 0; }
              to { transform: translateY(0); opacity: 1; }
            }
          `}
        </style>
      </>
    );
  }

  // Tabs (Uber-like hairline)
  function renderEditTabs() {
    return (
      <div style={styles.fullWidthToggleBar}>
        <div style={styles.behindLineBottom}></div>

        <div style={styles.mobileToggleBar}>
          {['Overview', 'Interests', 'Socials'].map((t, i) => (
            <div
              key={t}
              onClick={() => setActiveTab(i)}
              style={i === activeTab ? { ...styles.mobileTab, ...styles.mobileTabActive } : styles.mobileTab}
            >
              {t}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Content per tab
  const charLimit = 150;
  function renderTabContent() {
    switch (activeTab) {
      case 0:
        return (
          <div style={{ marginTop: 18 }}>
            <div style={styles.sectionLabel}>About</div>
            <div style={{ position: 'relative', height: aboutFocused ? 200 : 240, transition: 'height 0.3s ease' }}>
              <textarea
                style={{
                  ...styles.textArea,
                  width: '100%',
                  height: '100%',
                  overflowY: 'auto',
                  borderColor:
                    overview.trim().length === 0 || overview.trim().length >= MIN_ABOUT_LENGTH
                      ? '#e5e7eb'
                      : '#C22D2D',
                }}
                maxLength={charLimit}
                value={overview}
                placeholder="Share a concise intro that highlights your interests and vibe…"
                onChange={handleOverviewChange}
                onFocus={() => {
                  window.scrollTo({ top: 0, behavior: 'instant' });
                  setAboutFocused(true);
                }}
                onBlur={() => setAboutFocused(false)}
              />
              <div style={styles.charCountInside}>
                {overview.length}/{charLimit}
              </div>
              {overview.trim().length < MIN_ABOUT_LENGTH && (
                <div style={styles.validationBelow}>Min. {MIN_ABOUT_LENGTH} characters required.</div>
              )}
            </div>
          </div>
        );

      case 1: {
        const INTEREST_CHAR_LIMIT = 25;
        const canAdd =
          newInterest.trim().length > 0 && !interests.includes(newInterest.trim());
        return (
          <div style={{ marginTop: 18 }}>
            <div style={styles.sectionLabel}>Interests</div>

            {/* Compact pill input with inline Add button + live count */}
            <div style={styles.interestInputWrap}>
              <input
                type="text"
                placeholder="Add interest…"
                style={styles.interestInput}
                value={newInterest}
                maxLength={INTEREST_CHAR_LIMIT}
                onChange={(e) => setNewInterest(e.target.value)}
                onKeyDown={handleInterestKeyDown}
                onFocus={() => setAnyInputFocused(true)}
                onBlur={() => setAnyInputFocused(false)}
                aria-label="Add an interest"
              />

              <span style={styles.interestCountInline}>
                {newInterest.length}/{INTEREST_CHAR_LIMIT}
              </span>

              <button
                type="button"
                aria-label="Add interest"
                title="Add"
                onClick={() => {
                  if (canAdd) {
                    handleAddInterest(newInterest);
                    setNewInterest('');
                  }
                }}
                disabled={!canAdd}
                style={{
                  ...styles.interestAddBtn,
                  ...(canAdd ? {} : styles.interestAddBtnDisabled),
                }}
              >
                +
              </button>
            </div>

            {interests.length > 0 && (
              <div style={styles.chipsWrap}>
                {interests.map((i) => (
                  <span key={i} style={styles.activeChip}>
                    {i}
                    <button
  style={styles.removeChipBtn}
  onClick={() => handleRemoveInterest(i)}
  aria-label={`Remove ${i}`}
  title={`Remove ${i}`}
>
<LuX
  style={{
    width: 12,
    height: 12,
    display: 'block',
    color: '#555',   // ✅ explicitly define color here
  }}
/>
</button>
                  </span>
                ))}
              </div>
            )}

            <p style={styles.suggestedLabel}>Suggested</p>
            <div style={styles.chipsWrap}>
              {PRESET_INTERESTS.map((intr) => {
                const isSelected = interests.includes(intr);
                return (
                  <span
                    key={intr}
                    onClick={() => (isSelected ? handleRemoveInterest(intr) : handleAddInterest(intr))}
                    style={isSelected ? { ...styles.suggestedChip, ...styles.selectedSuggested } : styles.suggestedChip}
                  >
                    {interestEmojis[intr]} {intr}
                  </span>
                );
              })}
            </div>

            {interests.length < 3 && (
              <div style={{ marginTop: 10, fontSize: 12, color: '#6b7280' }}>
                Add at least <strong>3 interests</strong> to save.
              </div>
            )}
          </div>
        );
      }

      case 2:
        return (
          <div style={{ marginTop: 18 }}>
            <div style={styles.sectionLabel}>Instagram</div>
            <div style={styles.formInputRow}>
              <FaInstagram style={styles.socialIconSmall} />
              <span style={styles.atPrefix}>@</span>
              <input
                type="text"
                placeholder="username"
                style={styles.socialInput}
                value={socialHandles.instagram || ''}
                onChange={(e) => handlePrefixedChange('instagram', e.target.value)}
                onFocus={() => setAnyInputFocused(true)}
                onBlur={() => setAnyInputFocused(false)}
              />
            </div>


            <div style={styles.sectionLabel}>LinkedIn</div>
            <div style={styles.formInputRow}>
              <FaLinkedin style={styles.socialIconSmall} />
              <span style={styles.atPrefix}>@</span>
              <input
                type="text"
                placeholder="username"
                style={styles.socialInput}
                value={socialHandles.linkedin || ''}
                onChange={(e) => handlePrefixedChange('linkedin', e.target.value)}
                onFocus={() => setAnyInputFocused(true)}
                onBlur={() => setAnyInputFocused(false)}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  }

  const footerFocusStyle = anyInputFocused
    ? { transform: 'translateY(100%)', transition: 'transform 0.3s ease' }
    : { transform: 'translateY(0)', transition: 'transform 0.3s ease' };

  return (
    <>
      {/* Header */}
      <header style={headerContainerStyle}>
        <div style={headerLogoRowStyle}>
          <TribeLogo style={logoSvgStyle} />
        </div>

        <div style={headerNavRowStyle}>
          <button onClick={handleBack} style={previousBackButtonStyle}>
            <LuArrowLeft size={16} color="#fff" />
          </button>

          <div style={pageTitlePlainStyle}>Edit Profile</div>

          <div style={{ position: 'absolute', right: 16 }}>
            <EventHelpTooltip
              title="Editing your profile"
              body="Add a short bio (25+ chars) and at least 3 interests so others can find you."
            />
          </div>
        </div>
      </header>

      {/* Page */}
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.contentWrapper}>
            {/* Identity block */}
            {!aboutFocused && (
              <>
                <section style={styles.identityCard}>
                  <div style={styles.identityGrid}>
                    <div style={{ position: 'relative' }}>
                      <img src={profilePicUrl} alt="Profile" style={styles.profilePic} />
                      <button
  style={styles.pencilOverlay}
  onClick={() => setShowProfileActionSheet(true)}
  aria-label="Change photo"
>
  <span
    style={{
      width: 12,            // ← fixed pixels
      height: 12,           // ← fixed pixels
      display: 'block',
      lineHeight: 0,        // kill baseline
      fontSize: 0,          // neutralize 1em sizing
      flex: '0 0 18px',     // prevent flex from squeezing it
    }}
  >
    <LuPencil
      style={{ width: '100%', height: '100%', display: 'block' }}
      color="#555"
    />
  </span>
</button>

                      <input
                        ref={profilePicInputRef}
                        type="file"
                        style={{ display: 'none' }}
                        accept="image/*"
                        onChange={handleProfilePicChange}
                      />
                    </div>

                    <div style={styles.identityTextCol}>
                      <h1 style={styles.nameText}>
                        {user?.firstName} {user?.lastName ? user?.lastName.charAt(0) + '.' : ''}
                      </h1>
                      {(age || gender || user?.email) && (
  <div style={styles.metaLine}>
    {[age ? `${age}` : null, gender || null, user?.email || null]
      .filter(Boolean)
      .join(' • ')}
  </div>
)}
  <div style={styles.controlsRow}>
    <div style={styles.statusPill}>In Progress</div>
    {/* or: <div style={styles.statusPill}>Editing</div> */}
  </div>
</div>

                  </div>
                </section>

                {renderEditTabs()}
              </>
            )}

            <div style={{ marginTop: aboutFocused ? 8 : 20 }}>{renderTabContent()}</div>
          </div>
        </div>
      </div>

      {/* Fixed bottom action bar */}
      {!aboutFocused && (
        <>
          <div
            style={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              height: isPWA() ? '108px' : '72px',
              backgroundColor: '#fff',
              zIndex: 999,
              borderTop: '1px solid #eef0f2',
            }}
          />
          <div
            style={{
              ...styles.footerBar,
              ...footerFocusStyle,
              marginBottom: isPWA() ? 24 : 0,
              paddingBottom: isPWA() ? 'env(safe-area-inset-bottom)' : 12,
            }}
          >
            <button style={styles.cancelBtn} onClick={handleCancel}>
              Cancel
            </button>
            <button
              style={{
                ...styles.saveBtn,
                ...(interests.length < 3 || overview.trim().length < MIN_ABOUT_LENGTH
                  ? styles.saveBtnDisabled
                  : {}),
              }}
              disabled={interests.length < 3 || overview.trim().length < MIN_ABOUT_LENGTH}
              onClick={handleSave}
            >
              Save
            </button>
          </div>
        </>
      )}

      {/* Bottom sheet */}
      {renderProfileActionSheet()}

      {/* Unsaved changes */}
      {showUnsavedSheet && (
        <UnsavedChangesBottomSheet
          onCancel={() => setShowUnsavedSheet(false)}
          onDiscard={() => {
            setShowUnsavedSheet(false);
            navigate('/profile');
          }}
        />
      )}

      {/* Fade in */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
            @keyframes fadeIn {
              0% { opacity: 0; transform: translateY(10px); }
              100% { opacity: 1; transform: translateY(0); }
            }
          `,
        }}
      />
    </>
  );
}

/***************************************************************
 * STYLES — Aligned with Settings/UpdateAccount (Uber-like)
 ***************************************************************/
const styles = {
  page: {
    minHeight: '100vh',
    background: '#fff',
    color: '#0b0b0b',
    fontFamily: "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    animation: 'fadeIn 0.4s ease',
  },
  container: {
    maxWidth: 760,
    margin: '0px auto 24px',
    padding: '0 16px',
    width: '100%',
    boxSizing: 'border-box',
  },
  contentWrapper: {
    paddingBottom: 160, // space for fixed footer
  },
/* Identity header — match ProfilePage */
identityCard: {
  borderRadius: 12,
  background: '#FFFFFF',
  padding: 12,
  marginBottom: 14,
},
identityGrid: {
  display: 'grid',
  gridTemplateColumns: '96px 1fr',
  columnGap: 12,
  alignItems: 'center',
},
profilePic: {
  width: 96,
  height: 96,
  borderRadius: '50%',
  objectFit: 'cover',
  background: '#F3F4F6',
  border: '4px solid #ddd',
  marginLeft: -12,
},
pencilOverlay: {
  position: 'absolute',
  bottom: 4,
  right: 8,
  backgroundColor: '#fff',
  border: '1px solid #ddd',
  borderRadius: '50%',
  width: 28,
  height: 28,
  boxSizing: 'border-box',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 0,                 // kill baseline
  WebkitFontSmoothing: 'antialiased',
  WebkitTapHighlightColor: 'transparent',
},

identityTextCol: {
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
},
nameText: {
   margin: 0,
    fontSize: 'clamp(1.3rem, 2.2vw, 1.35rem)',
    fontWeight: 750,
    letterSpacing: '-0.005em',
    lineHeight: 1.5,
    color: '#0B1020',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
    marginTop: 8,
},
emailLine: {
  marginTop: 4,
  fontSize: 12.5,
  color: '#555',
  lineHeight: 1.5,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
},
metaLine: {
  marginTop: 4,
  fontSize: 12.5,
  color: '#555',
  lineHeight: 1.5,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
},

  /* Tabs */
  fullWidthToggleBar: {
    position: 'relative',
    marginTop: 2,
  },
  behindLineBottom: {
    position: 'absolute',
    bottom: 0,
    left: '50%',
    width: '100vw',
    marginLeft: '-50vw',
    borderBottom: '1px solid #eef0f2',
    zIndex: 0,
  },
  mobileToggleBar: {
    display: 'flex',
    justifyContent: 'space-around',
    gap: 40,
    position: 'relative',
    zIndex: 1,
  },
  mobileTab: {
    flex: 1,
    textAlign: 'center',
    padding: '10px 2px',
    cursor: 'pointer',
    color: '#6b7280',
    fontWeight: 800,
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  mobileTabActive: {
    color: '#0b0b0b',
    borderBottom: '1px solid #0b0b0b',
  },

  /* Section label */
  sectionLabel: {
    margin: '14px 2px 8px',
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: '#555',
    marginLeft: 5, // aligns after icon
  },

  /* About text area */
  textArea: {
    width: '100%',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    padding: 14,
    fontSize: 13,
    resize: 'vertical',
    fontFamily: "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    boxSizing: 'border-box',
    background: '#fff',
    outline: 'none',
  },
  charCountInside: {
    position: 'absolute',
    bottom: 12,
    right: 12,
    fontSize: 12,
    color: '#6b7280',
  },
  validationBelow: {
    position: 'absolute',
    bottom: -20,
    left: 8,
    color: '#C22D2D',
    fontSize: 12,
  },/* Interests — wall-to-wall integrated input + button */
  interestInputWrap: {
    display: 'flex',
    alignItems: 'center',
    width: '98%',
    height: 38,                      // compact height
    border: '1px solid #ddd',     // hairline
    borderRadius: 8,                // soft pill
    background: '#fff',
    paddingLeft: 12,                 // left padding for text
    overflow: 'hidden',              // keep children clipped to radius
    boxSizing: 'border-box',
    marginBottom: 16,
    height:36,
  },
  
  interestInput: {
    flex: 1,
    height: '100%',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontSize: 13,
    padding: 0,                      // let the wrapper handle spacing
    color: '#0b0b0b',
  },
  
  // inline counter as a segment inside the control
  interestCountInline: {
    fontSize: 12,
    color: '#9ca3af',
    padding: '0 10px',
    lineHeight: 1,
    whiteSpace: 'nowrap',
  },

interestAddBtn: {
  height: '100%',
  padding: '0 14px',
  border: 'none',
  borderLeft: '1px solid #e5e7eb', // subtle separator
  background: '#eceff1',            // active/enabled: a bit darker grey
  color: '#0b0b0b',
  fontSize: 13,
  fontWeight: 800,
  letterSpacing: 0.2,
  cursor: 'pointer',
  transition: 'background 120ms ease',
  
},

interestAddBtnDisabled: {
  background: '#f7f7f8', // disabled: very light grey
  color: '#bdbdbd',
  cursor: 'not-allowed',
},

  chipsWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  activeChip: {
    backgroundColor: '#f3f4f6',
    color: '#000',
    borderRadius: 999,
    padding: '4px 8px',            // slightly tighter
    fontSize: 12,
    lineHeight: 1,                 // ✅ kill baseline slack
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,                        // space between text and x
    border: '1px solid #ddd',
  },
  removeChipBtn: {
    // ✅ full reset for iOS
    appearance: 'none',
    WebkitAppearance: 'none',
    background: 'transparent',
    border: 'none',
    outline: 'none',
    padding: 0,
    margin: 0,
  
    // ✅ explicit box for perfect centering
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 18,
    height: 18,
    borderRadius: 9,
    lineHeight: 1,
  
    // visual
    fontSize: 14,
    color: '#000',
    cursor: 'pointer',
  
    // tap niceties (optional)
    WebkitTapHighlightColor: 'transparent',
  },
  suggestedLabel: {
    fontWeight: 900,
    fontSize: 12,
    marginBottom: 8,
    marginTop: 16,
    color: '#0b0b0b',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  suggestedChip: {
    padding: '6px 10px',
    borderRadius: 999,
    backgroundColor: '#fff',
    color: '#0b0b0b',
    fontSize: 12,
    cursor: 'pointer',
    border: '1px solid',
    borderColor: '#eeee',

  },
  selectedSuggested: {
    backgroundColor: '#e5e7eb',
    color: '#000',
    borderColor: '#eeee',
  },

  /* Social inputs */
  formInputRow: {
    display: 'flex',
    alignItems: 'center',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    padding: '8px 10px',
    marginBottom: 14,
    gap: 6,
    backgroundColor: '#fff',
  },
  socialIconSmall: {
    fontSize: 16,
    color: '#6b7280',
    flexShrink: 0,
  },
  socialInput: {
    flex: 1,
    border: 'none',
    outline: 'none',
    fontSize: 14,
    fontFamily: "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    height: 22,
  },

  /* Footer */
  footerBar: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    display: 'flex',
    gap: 10,
    padding: '12px 16px',
    zIndex: 1000,
  },
  cancelBtn: {
    flex: 1,
    border: '1px solid #d1d5db',
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 800,
    padding: '12px 0',
    cursor: 'pointer',
    backgroundColor: '#fff',
    color: '#0b0b0b',
  },
  saveBtn: {
    flex: 1,
    border: '1px solid #000',
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 800,
    padding: '12px 0',
    cursor: 'pointer',
    background: '#000',
    color: '#fff',
  },
  saveBtnDisabled: {
    background: '#bdbdbd',
    borderColor: '#bdbdbd',
    cursor: 'not-allowed',
  },

  /* Bottom sheet */
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.4)',
    zIndex: 9999,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-end',
    fontFamily: "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },
  sheetContainer: {
    width: '100%',
    maxWidth: 420,
    margin: '0 8px 8px',
    animation: 'slideUp 0.3s ease-out',
  },
  optionContainer: {
    backgroundColor: '#f9f9f9',
    borderRadius: 8,
    overflow: 'hidden',
  },
  optionRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '18px 16px',
    fontSize: 14,
    cursor: 'pointer',
    color: '#0b0b0b',
  },
  optionRowTop: {
    borderBottom: '1px solid #eee',
  },
  optionRowBottom: {},
  iconRight: {
    fontSize: 16,
    color: '#6b7280',
  },
  cancelRow: {
    marginTop: 12,
    backgroundColor: '#f0f0f0',
    borderRadius: 8,
    textAlign: 'center',
    padding: '14px 16px',
    fontSize: 12,
    fontWeight: 800,
    color: '#333',
    cursor: 'pointer',
    marginBottom: 1,
  },
  controlsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 8,
    alignSelf: 'start',
  },
  
  statusPill: {
    background: '#FFFFFF',
    color: '#555',
    border: '1px dotted #999', // dotted, same scale as ProfilePage edit button
    borderRadius: 10,
    padding: '6px 10px',
    fontSize: 11.5,
    fontWeight: 800,
    display: 'inline-flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    marginLeft: -4, // aligns with your edit button spacing on ProfilePage
  },
  atPrefix: {
    color: '#6b7280',
    marginLeft: 6,            // small space after the icon
    paddingLeft: 8,           // space after the divider before '@'
    borderLeft: '1px solid #E7E9EE', // the little grey line
    lineHeight: 1,            // keeps it nicely centered
  },
  };
